import { Controller } from "@hotwired/stimulus";

const W = 320;
const H = 200;

const LINE_WIDTHS = [78, 48, 96, 36, 66, 104, 54, 86, 42, 72, 60, 92];
const INDENTS = [0, 8, 8, 16, 8, 0, 8, 16, 8, 0, 8, 8];

const T = {
  type: 2500,
  reach: 3150,
  grip: 3400,
  lift: 4050,
  sip: 4950,
  lower: 5600,
  release: 5850,
  back: 6500,
  cycle: 8200,
};
const MUG_REST = { x: 60, y: 148 };
const MUG_MOUTH = { x: 132, y: 92 };
const MUG_H = 18;
const SHOULDER_L = { x: 120, y: 134 };
const SHOULDER_R = { x: 200, y: 134 };
const NECK = { x: 160, y: 118 };
const MAX_DELTA = 64; // clamp so a backgrounded tab resumes instead of jumping

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const ease = (t) => {
  t = clamp(t, 0, 1);
  return t * t * (3 - 2 * t);
};

export default class extends Controller {
  static targets = ["canvas"];
  static values = {
    robot: { type: String, default: "#374151" },
    robotDark: { type: String, default: "#1f2937" },
  };

  connect() {
    if (!this.hasCanvasTarget) return;

    this.ctx = this.canvasTarget.getContext("2d");
    this.render = this.render.bind(this);
    this.cleanups = [];
    this.clock = 0;
    this.lastFrame = null;
    this.onScreen = true;
    this.pageVisible = !document.hidden;

    this.scaleCanvas();
    this.readPalette();

    this.watchIntersection();
    this.watchPageVisibility();
    this.watchMotionPreference();
    this.watchPixelRatio();
    this.watchTheme();

    this.sync();
  }

  disconnect() {
    this.stop();
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    if (this.ctx) this.ctx.clearRect(0, 0, W, H);
    this.ctx = null;
    this.colors = null;
  }

  // --- lifecycle ---------------------------------------------------------

  sync() {
    if (!this.ctx) return;

    if (this.reducedMotion) {
      this.stop();
      if (this.onScreen) this.draw(0);
      return;
    }
    this.onScreen && this.pageVisible ? this.start() : this.stop();
  }

  start() {
    if (this.frame || !this.ctx) return;
    this.lastFrame = null;
    this.frame = requestAnimationFrame(this.render);
  }

  stop() {
    if (!this.frame) return;
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.lastFrame = null;
  }

  render(time) {
    if (!this.ctx) return;
    if (this.lastFrame !== null)
      this.clock += Math.min(time - this.lastFrame, MAX_DELTA);
    this.lastFrame = time;
    this.draw(this.clock);
    this.frame = requestAnimationFrame(this.render);
  }

  // --- observers ---------------------------------------------------------

  watchIntersection() {
    if (!window.IntersectionObserver) return;
    const observer = new IntersectionObserver(([entry]) => {
      this.onScreen = entry.isIntersecting;
      this.sync();
    });
    observer.observe(this.element);
    this.cleanups.push(() => observer.disconnect());
  }

  watchPageVisibility() {
    const handler = () => {
      this.pageVisible = !document.hidden;
      this.sync();
    };
    document.addEventListener("visibilitychange", handler);
    this.cleanups.push(() =>
      document.removeEventListener("visibilitychange", handler),
    );
  }

  watchMotionPreference() {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reducedMotion = query.matches;
    const handler = (event) => {
      this.reducedMotion = event.matches;
      this.sync();
    };
    query.addEventListener("change", handler);
    this.cleanups.push(() => query.removeEventListener("change", handler));
  }

  watchPixelRatio() {
    const handler = () => {
      if (!this.ctx || this.dpr === (window.devicePixelRatio || 1)) return;
      this.scaleCanvas();
      if (!this.frame) this.draw(this.clock);
    };
    window.addEventListener("resize", handler);
    this.cleanups.push(() => window.removeEventListener("resize", handler));
  }

  // repaint with the new palette when the theme switcher flips data-theme
  watchTheme() {
    if (!window.MutationObserver) return;
    const observer = new MutationObserver(() => {
      if (!this.ctx) return;
      this.readPalette();
      if (!this.frame) this.draw(this.clock);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });
    this.cleanups.push(() => observer.disconnect());
  }

  // --- setup -------------------------------------------------------------

  scaleCanvas() {
    this.dpr = window.devicePixelRatio || 1;
    this.canvasTarget.width = W * this.dpr;
    this.canvasTarget.height = H * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  readPalette() {
    const style = getComputedStyle(this.element);
    const read = (name, fallback) =>
      this.usable(style.getPropertyValue(name).trim(), fallback);

    this.colors = {
      b1: read("--color-base-100", "#f4f7f3"),
      b2: read("--color-base-200", "#e7f2e4"),
      b3: read("--color-base-300", "#d2dcd0"),
      accent: read("--color-warning", "#f59e0b"),
      bot: this.robotValue,
      botDark: this.robotDarkValue,
    };
  }

  // canvas 2d only got oklch() support recently — fall back if it chokes
  usable(value, fallback) {
    if (!value) return fallback;
    const previous = this.ctx.fillStyle;
    this.ctx.fillStyle = "#ff00ff";
    try {
      this.ctx.fillStyle = value;
    } catch {
      return fallback;
    }
    const resolved = this.ctx.fillStyle !== "#ff00ff" ? value : fallback;
    this.ctx.fillStyle = previous;
    return resolved;
  }

  // --- drawing helpers ---------------------------------------------------

  path(x, y, w, h, r) {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  fill(color, alpha = 1) {
    this.ctx.globalAlpha = alpha;
    this.ctx.fillStyle = color;
    this.ctx.fill();
  }

  stroke(color, alpha, width) {
    const ctx = this.ctx;
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  }

  // elbow bend scales with reach instead of a fixed offset, so short
  // (typing) arms tuck in against the torso and only flare when lifting
  arm(shoulder, hand, { squash = 0, gripping = false } = {}) {
    const ctx = this.ctx;
    const outward = shoulder.x < W / 2 ? -1 : 1;
    const reach = hand.x - shoulder.x;
    const raised = clamp((shoulder.y - hand.y) / 30, 0, 1);
    const bend = clamp(reach * 0.5, -26, 26) + outward * 14 * raised;
    const droop = 6 + Math.abs(reach) * 0.08;

    ctx.beginPath();
    ctx.moveTo(shoulder.x, shoulder.y);
    ctx.quadraticCurveTo(
      (shoulder.x + hand.x) / 2 + bend,
      (shoulder.y + hand.y) / 2 + droop,
      hand.x,
      hand.y,
    );
    this.stroke(this.colors.bot, 1, 7);

    // wrist rolls in depth with the mug, so the hand foreshortens too
    ctx.save();
    ctx.translate(hand.x, hand.y);
    ctx.scale(1, 1 - 0.14 * squash);
    ctx.beginPath();
    ctx.arc(0, 0, 5.4, 0, Math.PI * 2);
    this.fill(this.colors.botDark);
    if (gripping) {
      ctx.beginPath();
      ctx.moveTo(0, -4);
      ctx.lineTo(8, -3.2);
      this.stroke(this.colors.botDark, 1, 2.8);
      ctx.beginPath();
      ctx.moveTo(0, 2.5);
      ctx.lineTo(8, 3.2);
      this.stroke(this.colors.botDark, 1, 2.8);
    }
    ctx.restore();
  }

  // --- animation ---------------------------------------------------------

  liftAt(ms) {
    if (ms < T.grip) return 0;
    if (ms < T.lift) return ease((ms - T.grip) / (T.lift - T.grip));
    if (ms < T.sip) return 1;
    if (ms < T.lower) return 1 - ease((ms - T.sip) / (T.lower - T.sip));
    return 0;
  }

  // depth pitch: the mug tips its base away from us at the top of the lift.
  // drives foreshortening only — nothing rotates in the picture plane.
  pitchAt(ms) {
    const start = T.lift - 250;
    const settled = T.lift + 250;
    const releasing = T.sip - 150;
    const done = T.sip + 300;
    if (ms < start || ms >= done) return 0;
    if (ms < settled) return ease((ms - start) / (settled - start));
    if (ms < releasing) return 1;
    return 1 - ease((ms - releasing) / (done - releasing));
  }

  typingHand(now, side) {
    const wobble = Math.sin(now / 120) * 1.5;
    return side === "left"
      ? { x: 110 + wobble, y: 156 + Math.sin(now / 85 + 1.8) * 3 }
      : { x: 210 - wobble, y: 156 + Math.sin(now / 85) * 3 };
  }

  handAt(ms, now, grip) {
    const keys = this.typingHand(now, "left");
    if (ms < T.type || ms >= T.back) return keys;

    if (ms < T.reach) {
      const p = ease((ms - T.type) / (T.reach - T.type));
      return {
        x: keys.x + (grip.x - keys.x) * p,
        y: keys.y + (grip.y - keys.y) * p - 18 * Math.sin(Math.PI * p),
      };
    }
    if (ms < T.release) return grip;

    const p = ease((ms - T.release) / (T.back - T.release));
    return {
      x: grip.x + (keys.x - grip.x) * p,
      y: grip.y + (keys.y - grip.y) * p - 18 * Math.sin(Math.PI * p),
    };
  }

  draw(now) {
    const ctx = this.ctx;
    const { b1, b2, b3, accent, bot, botDark } = this.colors;

    const ms = now % T.cycle;
    const lift = this.liftAt(ms);
    const pitch = this.pitchAt(ms);
    const mug = {
      x: MUG_REST.x + (MUG_MOUTH.x - MUG_REST.x) * lift,
      y: MUG_REST.y + (MUG_MOUTH.y - MUG_REST.y) * lift,
    };

    // base swings away from the camera: body foreshortens, bottom stays put
    const bodyH = MUG_H - 3.4 * pitch;
    const bodyTop = mug.y + (MUG_H - bodyH);
    const bodyMid = bodyTop + bodyH / 2;
    const openRy = 1.6 + 1.7 * pitch;

    const grip = { x: mug.x - 3, y: bodyMid };
    const held = ms >= T.reach && ms < T.release;
    const hand = this.handAt(ms, now, grip);
    const bob = Math.sin(now / 150) * 0.8 * (1 - lift);

    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, H);

    // monitor + scrolling code
    this.path(52, 4, 216, 134, 10);
    this.fill(botDark);
    this.path(60, 12, 200, 118, 5);
    this.fill(b2);
    ctx.save();
    this.path(60, 12, 200, 118, 5);
    ctx.clip();
    const step = (now / 38) % 10;
    const offset = Math.floor(now / 380);
    for (let i = 0; i < 12; i++) {
      const idx = (i + offset) % LINE_WIDTHS.length;
      const y = 20 + i * 10 - step;
      this.path(64, y + 0.5, 3, 3, 1.5);
      this.fill(botDark, 0.25);
      this.path(74 + INDENTS[idx], y, LINE_WIDTHS[idx], 4, 2);
      this.fill(idx % 4 === 0 ? botDark : bot, idx % 4 === 0 ? 0.8 : 0.5);
      if (i === 6) {
        this.path(78 + INDENTS[idx] + LINE_WIDTHS[idx], y - 1.5, 2.5, 7, 1);
        this.fill(botDark, now % 900 < 450 ? 0.8 : 0.1);
      }
    }
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.moveTo(204, 12);
    ctx.lineTo(260, 12);
    ctx.lineTo(260, 56);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // desk surface + front panel
    ctx.globalAlpha = 1;
    ctx.fillStyle = b3;
    ctx.fillRect(0, 142, W, 36);
    ctx.fillStyle = b2;
    ctx.fillRect(0, 142, W, 3);
    ctx.fillStyle = b3;
    ctx.fillRect(0, 178, W, H - 178);
    ctx.globalAlpha = 0.1;
    ctx.fillStyle = botDark;
    ctx.fillRect(0, 178, W, H - 178);
    ctx.globalAlpha = 0.16;
    ctx.fillRect(0, 177, W, 1.5);

    // stand + keyboard
    ctx.globalAlpha = 1;
    this.path(150, 128, 20, 16, 3);
    this.fill(botDark);
    this.path(128, 140, 64, 7, 3.5);
    this.fill(botDark);
    this.path(94, 152, 132, 13, 3);
    this.fill(b1);
    this.stroke(botDark, 0.16, 1);
    for (let k = 0; k < 10; k++) {
      this.path(100 + k * 12, 155, 8, 3.5, 1.5);
      this.fill(botDark, 0.2);
    }

    // mug — bottom of the robot stack, every body part draws over it
    ctx.beginPath();
    ctx.ellipse(mug.x + 10, 167, 12 - 4 * lift, 3, 0, 0, Math.PI * 2);
    this.fill(botDark, 0.12 * (1 - lift));
    for (let s = 0; s < 3; s++) {
      const p = (now / 900 + s * 0.33) % 1;
      ctx.beginPath();
      for (let j = 0; j <= 6; j++) {
        const y = bodyTop - 3 - p * 12 - j * 2.2;
        const x = mug.x + 5 + s * 5 + Math.sin(j / 1.5 + now / 260 + s) * 2.2;
        j ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      this.stroke(botDark, 0.22 * (1 - p) * (1 - lift), 2);
    }

    ctx.beginPath();
    ctx.ellipse(mug.x - 1, bodyMid, 5, 5 - 0.8 * pitch, 0, 1.9, 4.4);
    this.stroke(botDark, 0.9, 2.5);
    this.path(mug.x, bodyTop, 20, bodyH, 3);
    this.fill(b1);
    this.stroke(botDark, 0.9, 2);

    // opening turns to face us as the base tips away
    ctx.beginPath();
    ctx.ellipse(
      mug.x + 10,
      bodyTop + openRy + 0.5,
      8.8,
      openRy,
      0,
      0,
      Math.PI * 2,
    );
    this.fill(botDark, 0.45);
    ctx.beginPath();
    ctx.ellipse(
      mug.x + 10,
      bodyTop + openRy + 1.1,
      7.2,
      Math.max(openRy - 1, 0.7),
      0,
      0,
      Math.PI * 2,
    );
    this.fill(accent, 0.8);

    // robot, from behind
    ctx.save();
    ctx.translate(0, bob);

    this.path(116, 118, 88, 92, 18);
    this.fill(bot);
    this.path(140, 132, 40, 40, 8);
    this.stroke(b1, 0.12, 1.5);
    ctx.save();
    ctx.beginPath();
    ctx.rect(112, 114, 96, 10);
    ctx.clip();
    this.path(116, 118, 88, 92, 18);
    this.stroke(b2, 0.35, 2.5);
    ctx.restore();

    this.arm(SHOULDER_L, hand, { squash: held ? pitch : 0, gripping: held });
    this.arm(SHOULDER_R, this.typingHand(now, "right"));

    // head dips a touch toward the mug — just enough to read as meeting it
    ctx.save();
    ctx.translate(0, 1.6 * pitch);
    ctx.translate(NECK.x, NECK.y);
    ctx.scale(1, 1 - 0.05 * pitch);
    ctx.translate(-NECK.x, -NECK.y);

    this.path(150, 104, 20, 16, 4);
    this.fill(botDark);
    this.path(122, 78, 9, 18, 3);
    this.fill(botDark);
    this.path(189, 78, 9, 18, 3);
    this.fill(botDark);
    this.path(130, 62, 60, 48, 14);
    this.fill(bot);
    this.stroke(b2, 0.45, 2.5);

    ctx.save();
    this.path(130, 62, 60, 48, 14);
    ctx.clip();
    this.path(130, 60, 60, 5 + 3 * pitch, 8);
    this.fill(b2, 0.1 + 0.08 * pitch);
    ctx.restore();

    this.path(142, 74, 36, 26, 6);
    this.stroke(b1, 0.14, 1.5);
    for (let v = 0; v < 3; v++) {
      this.path(149, 81 + v * 7, 22, 2.5, 1.2);
      this.fill(b1, 0.13);
    }
    ctx.beginPath();
    ctx.moveTo(160, 62);
    ctx.lineTo(160, 49.5 + 1.5 * pitch);
    this.stroke(bot, 1, 2.5);
    ctx.beginPath();
    ctx.arc(160, 46.5 + 1.5 * pitch, 3.2, 0, Math.PI * 2);
    this.fill(accent, 0.45 + 0.55 * Math.abs(Math.sin(now / 420)));
    ctx.restore();

    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
